import { Checkpoint } from '@langchain/langgraph-sdk'
import { t, Trans } from '@lingui/macro'
import { motion } from 'framer-motion'
import { ArrowDown, Loader2 } from 'lucide-react'
import React, { ReactNode, useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import { StringParam, useQueryParam } from 'use-query-params'
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom'
import { DO_NOT_RENDER_ID_PREFIX } from '../../lib/ensure-tool-responses'
import { cn } from '../../lib/utils'
import { useStreamContext } from '../../providers/Stream'
import { useAuthenticatedClient } from '../../providers/client'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Switch } from '../ui/switch'
import { Textarea } from '../ui/textarea'
import { useThreadCancel } from './hooks/useThreadCancel'
import { useThreadForm } from './hooks/useThreadForm'
import { useThreadState } from './hooks/useThreadState'
import { AssistantMessage, AssistantMessageLoading } from './messages/Ai'
import { HumanMessage } from './messages/Human'

// Extract constant to prevent recreation
const DEFAULT_API_URL = process.env.REACT_APP_FALCON_AGENT_API_URL as string

function StickyToBottomContent(props: { content: ReactNode; footer?: ReactNode; className?: string; contentClassName?: string }) {
    const context = useStickToBottomContext()
    return (
        <div ref={context.scrollRef} style={{ width: '100%', height: '100%', overflow: 'auto' }} className={props.className}>
            <div ref={context.contentRef} className={props.contentClassName}>
                {props.content}
            </div>

            {props.footer}
        </div>
    )
}

function ScrollToBottom(props: { className?: string }) {
    const { isAtBottom, scrollToBottom } = useStickToBottomContext()

    if (isAtBottom) return null
    return (
        <button
            style={{
                position: 'fixed',
                bottom: '200px',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '16px',
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: 'var(--foreground)',
                border: '1px solid var(--border)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                zIndex: 100
            }}
            onClick={() => scrollToBottom()}
            className={props.className}
        >
            <ArrowDown style={{ height: '16px', width: '16px' }} />
            <span>
                <Trans>Scroll to bottom</Trans>
            </span>
        </button>
    )
}

export function Thread() {
    // Custom hooks for clean separation of concerns
    const threadState = useThreadState()
    const stream = useStreamContext()
    const [threadId] = useQueryParam('threadId', StringParam)
    const client = useAuthenticatedClient(DEFAULT_API_URL)

    const { handleCancel } = useThreadCancel({
        client,
        threadId,
        currentRunId: stream.currentRunId
    })

    const { handleSubmit } = useThreadForm({
        input: threadState.input,
        isLoading: stream.isLoading,
        clearInput: threadState.clearInput,
        setFirstToken: threadState.setFirstToken,
        stream
    })

    const handleCharacterLimitExceeded = useCallback(() => {
        toast.error(t`Input exceeds the 8 KB limit`, {
            description: t`Please split your text into multiple messages or upload the file to your GitHub repository.`,
            richColors: true,
            closeButton: true
        })
    }, [])

    // Memoize textarea change handler
    const handleTextareaChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            threadState.setInput(e.target.value)
            // Auto-resize the textarea
            if (threadState.textareaRef.current) {
                threadState.textareaRef.current.style.height = '44px'
                threadState.textareaRef.current.style.height = `${Math.min(threadState.textareaRef.current.scrollHeight, 200)}px`
            }
        },
        [threadState]
    )

    // Memoize keydown handler
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                if (!stream.isLoading && threadState.input.length <= 7100) {
                    handleSubmit(e as any)
                } else if (threadState.input.length > 7100) {
                    handleCharacterLimitExceeded()
                }
            }
        },
        [stream.isLoading, handleSubmit, threadState.input.length, handleCharacterLimitExceeded]
    )

    // Error handling effect
    useEffect(() => {
        if (!stream.error) {
            threadState.lastError.current = undefined
            return
        }
        try {
            const message = (stream.error as any).message
            if (!message || threadState.lastError.current === message) {
                return
            }

            threadState.lastError.current = message
            toast.error(t`An error occurred. Please try again.`, {
                description: (
                    <p>
                        <strong>
                            <Trans>Error:</Trans>
                        </strong>{' '}
                        <code>{message}</code>
                    </p>
                ),
                richColors: true,
                closeButton: true
            })
        } catch {
            // no-op
        }
    }, [stream.error, threadState.lastError])

    // First token tracking effect
    useEffect(() => {
        if (
            stream.messages.length !== threadState.prevMessageLength.current &&
            stream.messages?.length &&
            stream.messages[stream.messages.length - 1].type === 'ai'
        ) {
            threadState.setFirstTokenReceived(true)
        }
        threadState.prevMessageLength.current = stream.messages.length
    }, [stream.messages, threadState])

    const handleRegenerate = useCallback(
        (parentCheckpoint: Checkpoint | null | undefined) => {
            // Do this so the loading state is correct
            threadState.prevMessageLength.current = threadState.prevMessageLength.current - 1
            threadState.setFirstTokenReceived(false)
            stream.submit(undefined, {
                checkpoint: parentCheckpoint,
                streamMode: ['values']
            })
        },
        [stream, threadState]
    )

    const hasNoAIOrToolMessages = !stream.messages.find((m) => m.type === 'ai' || m.type === 'tool')

    // Auto-resize textarea when mounted and when input changes
    useEffect(() => {
        if (threadState.textareaRef.current) {
            threadState.textareaRef.current.style.height = 'auto'
            threadState.textareaRef.current.style.height = `${threadState.textareaRef.current.scrollHeight}px`
        }
    }, [threadState.input, threadState.textareaRef])

    return (
        <>
            {/* Main content */}
            <div className='flex flex-col h-screen w-full overflow-auto justify-center'>
                <motion.div
                    className={cn('relative flex min-w-0 flex-col overflow-visible items-center')}
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        margin: '0 auto' // eslint-disable-line string-to-lingui/missing-lingui-transformation
                    }}
                >
                    {/* Simple Header */}
                    <div
                        style={{
                            width: '100%',
                            backgroundColor: 'white',
                            height: '48px',
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '8px',
                            marginBottom: '8px',
                            position: 'relative',
                            zIndex: 10
                        }}
                    >
                        <div
                            style={{
                                position: 'relative',
                                width: '100%',
                                maxWidth: '800px',
                                height: '100%',
                                padding: '0 16px'
                            }}
                        >
                            {/* Title centered */}
                            <div className='flex justify-center items-center h-full'>
                                <h1
                                    style={{
                                        fontSize: '28px',
                                        fontWeight: 800,
                                        letterSpacing: '-0.025em',
                                        color: '#111827',
                                        textAlign: 'center',
                                        lineHeight: 1.2
                                    }}
                                >
                                    <Trans>Ingest Agent</Trans>
                                </h1>
                            </div>
                        </div>
                    </div>

                    {/* Messages container */}
                    <div
                        className='flex justify-center w-full h-full'
                        style={{
                            flex: '1 1 auto',
                            width: '100%',
                            height: 'calc(100vh - 225px)', // Constrain height to viewport minus header and input
                            position: 'relative',
                            overflow: 'hidden' // Hide overflow at container level
                        }}
                    >
                        <StickToBottom
                            style={{
                                position: 'absolute',
                                inset: 0,
                                overflow: 'visible',
                                paddingTop: '16px',
                                paddingBottom: '120px',
                                width: '100%',
                                height: '100%' // Ensure full height
                            }}
                        >
                            <StickyToBottomContent
                                className='absolute inset-0 overflow-y-auto px-4'
                                contentClassName='w-full flex flex-col gap-8 items-center py-4' // eslint-disable-line string-to-lingui/missing-lingui-transformation
                                content={
                                    <>
                                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            {stream.messages
                                                .filter((m) => !m.id?.startsWith(DO_NOT_RENDER_ID_PREFIX))
                                                .map((message, index) =>
                                                    message.type === 'human' ? (
                                                        <div
                                                            key={message.id || `human-${index}`}
                                                            style={{
                                                                width: '100%',
                                                                maxWidth: '800px',
                                                                display: 'flex',
                                                                justifyContent: 'flex-end' // Right-justify human messages
                                                            }}
                                                        >
                                                            <HumanMessage message={message} isLoading={stream.isLoading} />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            key={message.id || `ai-${index}`}
                                                            style={{
                                                                width: '100%',
                                                                maxWidth: '800px',
                                                                display: 'flex',
                                                                justifyContent: 'flex-start' // Left-justify AI messages
                                                            }}
                                                        >
                                                            <AssistantMessage
                                                                message={message}
                                                                isLoading={stream.isLoading}
                                                                handleRegenerate={handleRegenerate}
                                                                hideToolCalls={threadState.hideToolCalls}
                                                            />
                                                        </div>
                                                    )
                                                )}

                                            {/* Special rendering case where there are no AI/tool messages, but there is an interrupt */}
                                            {hasNoAIOrToolMessages && !!stream.interrupt && (
                                                <div style={{ width: '100%', maxWidth: '800px' }}>
                                                    <AssistantMessage
                                                        message={undefined}
                                                        isLoading={stream.isLoading}
                                                        handleRegenerate={handleRegenerate}
                                                        hideToolCalls={threadState.hideToolCalls}
                                                    />
                                                </div>
                                            )}
                                            {stream.isLoading && !threadState.firstTokenReceived && (
                                                <div style={{ width: '100%', maxWidth: '800px' }}>
                                                    <AssistantMessageLoading />
                                                </div>
                                            )}
                                        </div>
                                    </>
                                }
                            />
                            <ScrollToBottom className='z-100' />
                        </StickToBottom>
                    </div>

                    {/* Input box - always fixed to bottom with fade gradient */}
                    <div
                        style={{
                            position: 'fixed',
                            bottom: 65,
                            left: 0,
                            right: 0,
                            zIndex: 90,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            backgroundColor: 'transparent',
                            padding: '16px 0'
                        }}
                    >
                        {/* White background behind the input */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'white',
                                zIndex: -1
                            }}
                        />

                        <div
                            style={{
                                width: '100%',
                                maxWidth: '800px',
                                padding: '0 16px',
                                margin: '0 auto'
                            }}
                        >
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%'
                                }}
                            >
                                <div
                                    style={{
                                        position: 'relative',
                                        zIndex: 10,
                                        margin: '0 auto',
                                        width: '100%',
                                        borderRadius: '16px',
                                        border: '1px solid var(--border)',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                        backgroundColor: 'var(--muted)'
                                    }}
                                >
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault()
                                            if (threadState.input.length > 7100) {
                                                handleCharacterLimitExceeded()
                                                return
                                            }
                                            handleSubmit(e as any)
                                        }}
                                        style={{
                                            margin: '0 auto',
                                            display: 'grid',
                                            width: '100%',
                                            gridTemplateRows: '1fr auto',
                                            gap: '0px'
                                        }}
                                    >
                                        <div className='w-full'>
                                            <Textarea
                                                ref={threadState.textareaRef}
                                                placeholder={t`Type your message...`}
                                                spellCheck={false}
                                                required
                                                style={{
                                                    resize: 'none',
                                                    border: 'none',
                                                    outline: 'none',
                                                    backgroundColor: 'transparent',
                                                    padding: '10px 16px',
                                                    fontFamily: 'inherit',
                                                    fontSize: '16px',
                                                    lineHeight: '1.5',
                                                    width: '100%',
                                                    height: '44px',
                                                    minHeight: '44px',
                                                    maxHeight: '200px',
                                                    overflow: 'auto'
                                                }}
                                                value={threadState.input}
                                                onChange={handleTextareaChange}
                                                onKeyDown={handleKeyDown}
                                            />
                                            <div
                                                style={{
                                                    textAlign: 'right',
                                                    fontSize: '12px',
                                                    padding: '8px 16px',
                                                    margin: '4px 0',
                                                    color: '#64748b'
                                                }}
                                            >
                                                <span>{threadState.input.length}</span>
                                                <Trans> / 7100 chars</Trans>
                                                {threadState.input.length > 7100 && (
                                                    <>
                                                        {' '}
                                                        <Trans>(exceeds 8 KB content limit)</Trans>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '0px 12px 8px'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                <Switch
                                                    id='hide-tool-calls-chat'
                                                    checked={threadState.hideToolCalls}
                                                    onCheckedChange={threadState.setHideToolCalls}
                                                />
                                                <Label htmlFor='hide-tool-calls-chat'>
                                                    <Trans>Hide Tool Calls</Trans>
                                                </Label>
                                            </div>
                                            {stream.isLoading ? (
                                                <Button
                                                    type='button'
                                                    variant='destructive'
                                                    onClick={handleCancel}
                                                    className='inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium h-9 px-4 py-2 transition-colors'
                                                >
                                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                                    <Trans>Cancel</Trans>
                                                </Button>
                                            ) : (
                                                <Button
                                                    disabled={threadState.input === ''}
                                                    type='submit'
                                                    className='inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 transition-colors'
                                                >
                                                    <Trans>Send</Trans>
                                                </Button>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </>
    )
}
